import { useCallback, useEffect, useRef, useState } from 'react'

import type { InputProps } from '../input'

import { Input } from '../input'
import { T } from '../t'
import { formatDate, parseIsoDate, validateDateFormat } from '../utils'

type Props = Omit<InputProps, 'onChange'> & {
  initialDate?: Date
  onChange: (date?: Date) => void
  onValidate: (date?: Date) => string
  setValidateStatus: (inValid: boolean) => void
}

const isValidDate = (dateString: string) => !!validateDateFormat(dateString) && !!parseIsoDate(dateString)

export const DateInput = ({ initialDate, onChange, onValidate, setValidateStatus, autoFocus, ...rest }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dateInput, setDateInput] = useState<string>(
    initialDate ? formatDate(initialDate, 'YYYY-MM-DD', { timeZone: 'UTC' }) : '',
  )
  const [dateInputValidated, setDateInputValidated] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleOnBlur = useCallback(() => {
    setDateInputValidated(!dateInput)
    const parsedDate = parseIsoDate(dateInput)
    if (parsedDate) {
      setDateInput(formatDate(parsedDate, 'YYYY-MM-DD', { timeZone: 'UTC' }))
      const validateMessage = onValidate(parsedDate)
      if (!validateMessage) onChange(parsedDate)
      setDateInputValidated(!validateMessage)
      setErrorMessage(validateMessage)
    }
  }, [dateInput, onValidate, onChange])

  useEffect(() => {
    const formatted = initialDate ? formatDate(initialDate, 'YYYY-MM-DD', { timeZone: 'UTC' }) : ''
    setDateInput(formatted)
    const validation = onValidate(initialDate)
    setDateInputValidated(!validation)
    setValidateStatus(!validation)
    setErrorMessage(validation)
  }, [initialDate, onValidate, setValidateStatus])

  useEffect(() => {
    if (dateInput) {
      setValidateStatus(isValidDate(dateInput))
      if (!validateDateFormat(dateInput)) {
        setErrorMessage('Supported format is YYYY-MM-DD')
      } else {
        const parsedDate = parseIsoDate(dateInput)
        if (!parsedDate) {
          setErrorMessage('Invalid date')
        } else {
          const validateMessage = onValidate(parsedDate)
          if (!validateMessage) onChange(parsedDate)
          setDateInputValidated(!validateMessage)
          setErrorMessage(validateMessage)
        }
      }
    }
  }, [dateInput, onValidate, onChange, setValidateStatus])

  // native autoFocus scrolls the page when inside a FloatingPortal; use preventScroll instead
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus({ preventScroll: true })
  }, [])

  return (
    <Input
      className="dateInput"
      data-testid="test-date-input"
      errorText={!dateInputValidated && <T size="md">{errorMessage}</T>}
      inputRef={inputRef}
      onBlur={handleOnBlur}
      onChange={e => setDateInput(e.target.value)}
      placeholder="YYYY-MM-DD"
      size="md"
      state={!dateInputValidated ? 'error' : 'default'}
      value={dateInput}
      {...rest}
    />
  )
}
